/**
 * GA4 custom event tracking for fuguelab.com
 * Tracks booking CTAs, form submissions, and outbound links.
 *
 * WHY `sanitize` EXISTS (2026-09-04). Every helper below used to pass a param
 * literally named `source`, and `source` is a GA4 RESERVED MANUAL-CAMPAIGN
 * PARAMETER: `gtag('event', x, { source })` does not attach a custom dimension,
 * it OVERWRITES the session's traffic source. Measured on property 527738893 —
 * 105 sessions attributed to `/packages` and 2 to `/pricing`, tracking the
 * literal `trackPricingView` happened to be passing at the time. `blog_view`
 * was rewriting sessions to `blog` the same way.
 *
 * This is the `arguments`-vs-array bug's twin (see @gjl/analytics's provider):
 * the tag loads, /g/collect fires, the console looks right — and the number at
 * the other end is wrong. It is the worse of the two, because the first one
 * produced NO data while this one produces PLAUSIBLE data. `trackBookingComplete`
 * was the sharpest instance: it rewrote the source at the moment of conversion,
 * so the one event that should say which channel produced a booking was the one
 * guaranteed to misattribute it.
 *
 * Two defences, deliberately. `sanitize` is the runtime one and catches keys
 * arriving from a variable; `scripts/ci/ga4-guard.mjs` rule 3 is the static one
 * and is what actually prevents recurrence. Strip rather than throw: an
 * analytics helper must never be able to break a page.
 *
 * The historical data is NOT recoverable — `sessionSource` is immutable once
 * processed. Traffic-source reports spanning 2026-09 need `^/` and `blog`
 * excluded by hand.
 */

// `window.gtag` is typed by @gjl/analytics's ambient `Gtag` global, which is in
// the compile via @gjl/site-kit. A local Window.gtag declaration here would
// conflict with it (merged-global types must be identical), so we rely on theirs.

/**
 * GA4 manual-campaign parameters. Any of these on a custom event rewrites the
 * session's attribution. `event_source` is our replacement for `source` — it
 * pairs with the `event_category` already on every event here, and one name
 * means one custom dimension to register rather than five.
 */
const RESERVED_PARAMS = new Set([
  'source',
  'medium',
  'campaign',
  'campaign_id',
  'term',
  'content',
  'source_platform',
  'creative_format',
  'marketing_tactic',
])

/** PII must never reach GA4 — it is against Google's terms and can get a property purged. */
const PII_PARAMS = new Set(['email', 'user_email', 'phone', 'phone_number', 'first_name', 'last_name', 'address'])

function sanitize(params: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (RESERVED_PARAMS.has(key) || PII_PARAMS.has(key)) {
      if (import.meta.env.DEV) {
        console.warn(
          `[analytics] dropped reserved/PII GA4 param "${key}". ` +
            `Reserved names silently overwrite session attribution; use event_source instead.`
        )
      }
      continue
    }
    clean[key] = value
  }
  return clean
}

/**
 * Fire a GA4 event. Exported so blog-analytics.ts shares ONE filter rather than
 * keeping a second copy of the same three lines (which is how `source: 'blog'`
 * survived the first pass over this file).
 */
export function track(eventName: string, params: Record<string, unknown>) {
  window.gtag?.('event', eventName, sanitize(params))
}

/** Track when a visitor clicks a "book a call" CTA (routes to the /book scheduler) */
export function trackCalendlyClick(eventSource: string) {
  track('booking_cta_click', {
    link_url: '/book',
    event_category: 'booking',
    event_source: eventSource,
  })
}

/** Track when the self-hosted booking widget loads (visitor sees available times) */
export function trackBookingView(bookingType: string, eventSource: string, slotCount: number) {
  track('booking_widget_view', {
    event_category: 'booking',
    booking_type: bookingType,
    event_source: eventSource,
    slot_count: slotCount,
  })
}

/**
 * Track when a visitor picks a time. THE engagement denominator: unlike
 * `booking_widget_view`, which fires wherever the widget merely mounts, this
 * one requires a deliberate act.
 */
export function trackBookingSlotSelected(bookingType: string, eventSource: string, daysAhead: number) {
  track('booking_slot_selected', {
    event_category: 'booking',
    booking_type: bookingType,
    event_source: eventSource,
    days_ahead: daysAhead,
  })
}

/** Track a booking submit attempt (fires on every attempt — retries are the signal). */
export function trackBookingSubmit(bookingType: string, eventSource: string, attempt: number) {
  track('booking_submit', {
    event_category: 'booking',
    booking_type: bookingType,
    event_source: eventSource,
    attempt,
  })
}

/**
 * Track a booking failure. NOT named `error` — that is a GA4 reserved EVENT
 * name (the app-exception slot), the same class of mistake as `source` above.
 */
export function trackBookingError(
  bookingType: string,
  eventSource: string,
  reason: 'availability_load' | 'slot_conflict' | 'unknown',
  attempt: number
) {
  track('booking_error', {
    event_category: 'booking',
    booking_type: bookingType,
    event_source: eventSource,
    error_reason: reason,
    attempt,
  })
}

/** Track when someone completes a booking via the self-hosted widget */
export function trackBookingComplete(bookingType: string, eventSource: string, daysAhead: number) {
  track('booking_complete', {
    event_category: 'conversion',
    booking_type: bookingType,
    event_source: eventSource,
    days_ahead: daysAhead,
  })
}

/** Track contact form submission */
export function trackFormSubmit(formName: string, email: string) {
  track('form_submit', {
    form_name: formName,
    event_category: 'lead',
    // Don't send PII to GA4, just that a submission happened
    has_email: !!email,
  })
}

/** Track when a visitor views a pricing page (packages or care plan) */
export function trackPricingView(eventSource: string) {
  track('pricing_view', {
    event_category: 'engagement',
    event_source: eventSource,
  })
}

/** Track when a visitor views a specific portfolio item */
export function trackPortfolioView(slug: string) {
  track('portfolio_view', {
    event_category: 'engagement',
    portfolio_slug: slug,
  })
}

/** Track page engagement (scroll depth + time on page) */
export function trackPageEngagement(page: string, timeOnPageSeconds: number, scrollDepthPercent: number) {
  track('page_engagement', {
    event_category: 'engagement',
    page_path: page,
    time_on_page_seconds: Math.round(timeOnPageSeconds),
    scroll_depth_percent: scrollDepthPercent,
  })
}

/** Track return visits (same device, localStorage-based) */
export function trackReturnVisit(visitNumber: number) {
  track('return_visit', {
    event_category: 'engagement',
    visit_number: visitNumber,
  })
}

/** Track when a visitor clicks the "Tell Us About Your Project" CTA on the welcome page */
export function trackGetStartedClick(eventSource: string) {
  track('get_started_click', {
    event_category: 'lead',
    event_source: eventSource,
  })
}

/** Track when the intake form pre-fills orgType from UTM params (outreach attribution) */
export function trackFormPrefill(utmSlug: string) {
  track('form_prefill', {
    event_category: 'lead',
    utm_content: utmSlug,
  })
}

/** Track when a visitor requests a free website audit from the welcome page */
export function trackAuditRequest(eventSource: string, email?: string, orgName?: string) {
  track('audit_request', {
    event_category: 'lead',
    event_source: eventSource,
    // Never the address itself — `has_email` is the sanctioned form (see trackFormSubmit).
    has_email: !!email,
    ...(orgName && { org_name: orgName }),
  })
}

/**
 * Global click handler for outbound booking links.
 * Attach once in main.tsx — catches all <a> clicks to calendly.com.
 */
export function initOutboundClickTracking() {
  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
    if (!link) return

    const href = link.href
    if (href.includes('calendly.com')) {
      trackCalendlyClick(window.location.pathname)
    }
  })
}
